// 全局动作：Toast / 确认框 / 创建表单 / 导出 CSV
import {
  createContext, useContext, useState, useCallback, useRef, type ReactNode,
} from 'react';
import { mutate } from './api/client';

// ====== 类型 ======
type ToastType = 'info' | 'success' | 'error';
interface ToastItem { id: number; msg: string; type: ToastType; }
interface ConfirmState { msg: string; onOk: () => void; }
interface CreateState { slug: string; }

interface ActionsCtx {
  toast: (msg: string, type?: ToastType) => void;
  openCreate: (slug: string) => void;
  openConfirm: (msg: string, onOk: () => void) => void;
}

const Ctx = createContext<ActionsCtx | null>(null);

export function useActions() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useActions must be used within ActionProvider');
  return c;
}

// ====== 创建表单字段定义 ======
export interface FieldDef {
  key: string;
  label: string;
  type?: 'text' | 'number' | 'select' | 'textarea';
  options?: string[];
  placeholder?: string;
  default?: string | number;
}
interface FormMeta { title: string; fields: FieldDef[]; }

const DEPTS = ['产品研发部', '算法团队', '设计中心', '数据中台', 'HR 中心', '运营中心'];

const CREATE_FIELDS: Record<string, FormMeta> = {
  needs: {
    title: '新建招聘需求',
    fields: [
      { key: 'dept', label: '部门', type: 'select', options: DEPTS, default: DEPTS[0] },
      { key: 'position', label: '岗位', placeholder: '如：高级前端工程师' },
      { key: 'count', label: '人数', type: 'number', default: 1 },
      { key: 'priority', label: '优先级', type: 'select', options: ['P0', 'P1', 'P2'], default: 'P1' },
      { key: 'budget', label: '预算', placeholder: '如：40-55K·15薪' },
      { key: 'recruiter', label: 'HR', placeholder: '负责人' },
      { key: 'target', label: '目标到岗', placeholder: '2026-08-30' },
    ],
  },
  reviews: {
    title: '发起需求评审',
    fields: [
      { key: 'position', label: '岗位', placeholder: '岗位名称' },
      { key: 'dept', label: '部门', type: 'select', options: DEPTS, default: DEPTS[0] },
      { key: 'reviewer', label: '审核人', placeholder: '如：张总 + 财务' },
      { key: 'headcount', label: 'HC', type: 'number', default: 1 },
      { key: 'budget', label: '预算', placeholder: '如：120万/年' },
      { key: 'remark', label: '备注', type: 'textarea' },
    ],
  },
  jds: {
    title: '新建 JD',
    fields: [
      { key: 'title', label: '岗位名称', placeholder: '如：高级前端工程师' },
      { key: 'dept', label: '部门', placeholder: '部门' },
      { key: 'level', label: '职级', placeholder: '如：P6-P7' },
      { key: 'salary', label: '薪资', placeholder: '如：40-55K·15薪' },
      { key: 'location', label: '地点', placeholder: '如：北京' },
      { key: 'report_to', label: '汇报对象', placeholder: '如：前端 Leader' },
    ],
  },
  channels: {
    title: '新增招聘渠道',
    fields: [
      { key: 'name', label: '渠道名称', placeholder: '如：Boss直聘' },
      { key: 'type', label: '类型', placeholder: '如：综合招聘平台' },
      { key: 'short', label: '简称', placeholder: '2字以内' },
      { key: 'status', label: '定位', type: 'select', options: ['主投', '辅助', '储备'], default: '辅助' },
      { key: 'color', label: '主题色', type: 'select', options: ['cinnabar', 'bamboo', 'gold', 'ink'], default: 'ink' },
    ],
  },
  'channel-posts': {
    title: '发布岗位到渠道',
    fields: [
      { key: 'position', label: '岗位', placeholder: '岗位名称' },
      { key: 'channels', label: '发布渠道', placeholder: '如：Boss, 猎聘, 内推' },
      { key: 'status', label: '状态', type: 'select', options: ['在线', '审核中'], default: '在线' },
    ],
  },
  interviews: {
    title: '发起面试邀约',
    fields: [
      { key: 'name', label: '候选人', placeholder: '姓名' },
      { key: 'role', label: '应聘岗位', placeholder: '岗位' },
      { key: 'round', label: '轮次', type: 'select', options: ['r1', 'r2', 'r3', 'r4', 'r5'], default: 'r1' },
      { key: 'time', label: '时间', placeholder: '如：07-08 10:00' },
      { key: 'interviewer', label: '面试官', placeholder: '面试官' },
    ],
  },
  'assessment-records': {
    title: '发起测评',
    fields: [
      { key: 'name', label: '候选人', placeholder: '姓名' },
      { key: 'type', label: '测评类型', placeholder: '如：笔试 · 编程' },
      { key: 'score', label: '得分/结果', placeholder: '如：90' },
      { key: 'time', label: '用时', placeholder: '如：60 min' },
    ],
  },
  'salary-approvals': {
    title: '发起薪酬审批',
    fields: [
      { key: 'name', label: '候选人', placeholder: '姓名' },
      { key: 'position', label: '岗位', placeholder: '岗位' },
      { key: 'level', label: '职级', type: 'select', options: ['P5', 'P6', 'P7', 'P8'], default: 'P6' },
      { key: 'base', label: '基础月薪', type: 'number', placeholder: '如：38000' },
      { key: 'bonus', label: '奖金', placeholder: '如：14薪' },
      { key: 'stock', label: '股票', placeholder: '如：200股/年' },
      { key: 'allowance', label: '补贴', placeholder: '如：1200/月' },
      { key: 'total', label: '年包', placeholder: '如：约 54W/年' },
    ],
  },
  'bg-checks': {
    title: '发起背景调查',
    fields: [
      { key: 'name', label: '候选人', placeholder: '姓名' },
      { key: 'company', label: '前公司', placeholder: '公司名' },
      { key: 'period', label: '在职时间', placeholder: '如：2019.07 - 至今' },
      { key: 'position', label: '职位', placeholder: '职位' },
      { key: 'verifier', label: '核验人', placeholder: '前 Leader' },
      { key: 'items', label: '核验项', placeholder: '如：履历 · 学历 · 竞业' },
    ],
  },
  offers: {
    title: '制作 Offer',
    fields: [
      { key: 'name', label: '候选人', placeholder: '姓名' },
      { key: 'position', label: '岗位', placeholder: '岗位' },
      { key: 'salary', label: '薪酬方案', placeholder: '如：52K·15薪' },
      { key: 'onboard', label: '预计入职', placeholder: '如：08-01' },
    ],
  },
  'pre-onboard': {
    title: '新增入职前跟进',
    fields: [
      { key: 'name', label: '候选人', placeholder: '姓名' },
      { key: 'original', label: '原公司', placeholder: '公司名' },
      { key: 'leave_date', label: '预计离职', placeholder: '如：07-26' },
      { key: 'days_to_go', label: '距离天数', type: 'number', default: 14 },
      { key: 'risk', label: '风险', type: 'select', options: ['低', '中', '高'], default: '低' },
    ],
  },
  'new-hires': {
    title: '新员工报到',
    fields: [
      { key: 'name', label: '员工姓名', placeholder: '姓名' },
      { key: 'position', label: '岗位', placeholder: '岗位' },
      { key: 'dept', label: '部门', type: 'select', options: DEPTS, default: DEPTS[0] },
      { key: 'mentor', label: '导师', placeholder: '导师姓名' },
      { key: 'date', label: '入职日期', placeholder: '如：08-01' },
    ],
  },
  'probation-logs': {
    title: '发起月度复盘',
    fields: [
      { key: 'date', label: '日期', placeholder: '如：07-05' },
      { key: 'type', label: '类型', type: 'select', options: ['月度复盘', '导师反馈', '跨部门协作', '入职引导'], default: '月度复盘' },
      { key: 'desc', label: '内容', type: 'textarea' },
    ],
  },
  budget: {
    title: '调整预算',
    fields: [
      { key: 'name', label: '预算项', placeholder: '如：Boss直聘会员' },
      { key: 'planned', label: '预算总额', type: 'number', placeholder: '如：20000' },
      { key: 'used', label: '已使用', type: 'number', default: 0 },
      { key: 'color', label: '主题色', type: 'select', options: ['cinnabar', 'bamboo', 'gold', 'ink'], default: 'cinnabar' },
    ],
  },
  activity: {
    title: '主动激活',
    fields: [
      { key: 'time', label: '时间', placeholder: '如：14:00' },
      { key: 'text', label: '动态内容', type: 'textarea', placeholder: '如：张三 加入人才库' },
    ],
  },
};

// ====== CSV 导出 ======
export function exportCsv(rows: Record<string, any>[], filename: string) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const esc = (v: any) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const csv = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => esc(r[h])).join(',')),
  ].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ====== Provider ======
export function ActionProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [create, setCreate] = useState<CreateState | null>(null);
  const tid = useRef(0);

  const toast = useCallback((msg: string, type: ToastType = 'info') => {
    const id = ++tid.current;
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2600);
  }, []);

  const openCreate = useCallback((slug: string) => setCreate({ slug }), []);
  const openConfirm = useCallback((msg: string, onOk: () => void) => setConfirm({ msg, onOk }), []);

  return (
    <Ctx.Provider value={{ toast, openCreate, openConfirm }}>
      {children}
      <ToastHost toasts={toasts} />
      <ConfirmHost confirm={confirm} onClose={() => setConfirm(null)} />
      <ModalHost create={create} onClose={() => setCreate(null)} toast={toast} />
    </Ctx.Provider>
  );
}

function ToastHost({ toasts }: { toasts: ToastItem[] }) {
  return (
    <div className="toast-host">
      {toasts.map((t) => (
        <div key={t.id} className={'toast toast-' + t.type}>{t.msg}</div>
      ))}
    </div>
  );
}

function ConfirmHost({ confirm, onClose }: { confirm: ConfirmState | null; onClose: () => void }) {
  if (!confirm) return null;
  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-body">{confirm.msg}</div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>取消</button>
          <button className="btn btn-primary" onClick={() => { confirm.onOk(); onClose(); }}>确定</button>
        </div>
      </div>
    </div>
  );
}

function ModalHost({
  create, onClose, toast,
}: { create: CreateState | null; onClose: () => void; toast: (m: string, t?: ToastType) => void }) {
  if (!create) return null;
  return (
    <div className="modal-mask" onClick={onClose}>
      <CreateForm key={create.slug} slug={create.slug} onClose={onClose} toast={toast} />
    </div>
  );
}

function CreateForm({
  slug, onClose, toast,
}: { slug: string; onClose: () => void; toast: (m: string, t?: ToastType) => void }) {
  const meta = CREATE_FIELDS[slug];
  const [values, setValues] = useState<Record<string, any>>(() => {
    const init: Record<string, any> = {};
    if (meta) for (const f of meta.fields) if (f.default !== undefined) init[f.key] = f.default;
    return init;
  });
  const [saving, setSaving] = useState(false);

  if (!meta) return null;

  const submit = async () => {
    setSaving(true);
    try {
      await mutate.create(slug, values);
      toast('已创建', 'success');
      onClose();
    } catch (e: any) {
      toast(e?.message || '创建失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal" onClick={(e) => e.stopPropagation()}>
      <div className="modal-head">
        <h3>{meta.title}</h3>
        <button className="modal-x" onClick={onClose}>×</button>
      </div>
      <div className="modal-body">
        {meta.fields.map((f) => (
          <div className="form-row" key={f.key}>
            <label>{f.label}</label>
            {f.type === 'textarea' ? (
              <textarea
                value={values[f.key] ?? ''}
                placeholder={f.placeholder}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
              />
            ) : f.type === 'select' ? (
              <select
                value={values[f.key] ?? f.default ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
              >
                <option value="">请选择</option>
                {f.options!.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input
                type={f.type === 'number' ? 'number' : 'text'}
                value={values[f.key] ?? ''}
                placeholder={f.placeholder}
                onChange={(e) => setValues((v) => ({
                  ...v,
                  [f.key]: f.type === 'number' ? Number(e.target.value) : e.target.value,
                }))}
              />
            )}
          </div>
        ))}
      </div>
      <div className="modal-foot">
        <button className="btn btn-ghost" onClick={onClose}>取消</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>
          {saving ? '提交中…' : '提交'}
        </button>
      </div>
    </div>
  );
}